import { type ReactNode, useMemo } from 'react';
import { useVideoConfig } from '@/hooks/use-video-config';
import { useRequiredVideo } from '@/hooks/use-required-video';
import {
  RequiredVideoCtx,
  type RequiredVideoCtxValue,
} from './required-video-context';
import type {
  RequiredVideoConfig,
  VideoProvider,
} from '@/types/required-video';

const FALLBACK: RequiredVideoConfig = {
  video_key: '__loading__',
  provider: 'youtube',
  video_id: '',
  threshold: 1,
  auth_required: false,
};

type ServerProps = {
  videoKey: string;
  onWatched: () => void;
  children: ReactNode;
};

type LocalProps = {
  localKey: string;
  provider: VideoProvider;
  videoId: string;
  threshold?: number;
  onWatched: () => void;
  children: ReactNode;
};

type Props = ServerProps | LocalProps;

type InnerProps = {
  config: RequiredVideoConfig | null;
  mode: 'server' | 'local';
  onWatched: () => void;
  loading: boolean;
  error: string | null;
  children: ReactNode;
};

function Inner(p: InnerProps): ReactNode {
  const safe = p.config ?? FALLBACK;
  const state = useRequiredVideo({
    config: safe,
    mode: p.mode,
    onWatched: p.onWatched,
  });
  // Loading is true until BOTH the config and the saved progress have
  // landed. Without progress, the iframe would mount with last_position=0
  // (seek-on-ready misses) and the caption would flash the full duration.
  const loading = p.loading || state.progressLoading;
  const value: RequiredVideoCtxValue = useMemo(
    () => ({ state, config: p.config, loading, error: p.error }),
    [state, p.config, loading, p.error]
  );
  return (
    <RequiredVideoCtx.Provider value={value}>
      {p.children}
    </RequiredVideoCtx.Provider>
  );
}

function ServerHost(p: ServerProps): ReactNode {
  const { config, loading, error } = useVideoConfig(p.videoKey);
  return (
    <Inner
      config={config}
      mode="server"
      onWatched={p.onWatched}
      loading={loading}
      error={error}
    >
      {p.children}
    </Inner>
  );
}

function buildLocalConfig(p: LocalProps): RequiredVideoConfig {
  return {
    video_key: p.localKey,
    provider: p.provider,
    video_id: p.videoId,
    threshold: p.threshold ?? 1,
    auth_required: false,
  };
}

function LocalHost(p: LocalProps): ReactNode {
  const config = useMemo(() => buildLocalConfig(p), [p]);
  return (
    <Inner
      config={config}
      mode="local"
      onWatched={p.onWatched}
      loading={false}
      error={null}
    >
      {p.children}
    </Inner>
  );
}

export function RequiredVideo(props: Props): ReactNode {
  if ('videoKey' in props) return <ServerHost {...props} />;
  return <LocalHost {...props} />;
}
